import { Abstract, BadgeType } from '@darkforest_eth/types';
import color from 'color';
import { css, html, LitElement, nothing, svg, TemplateResult, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import dfstyles from './styles';

export type BadgeElement = {
  name: string;
  description: string;
  badge: TemplateResult<2>;
  color:
    | 'gold'
    | 'silver'
    | 'bronze'
    | 'red'
    | 'yellow'
    | 'orange'
    | 'green'
    | 'blue'
    | 'indigo'
    | 'violet';
};

export class DarkForestBadge extends LitElement {
  // Not part of LitElement but let's tack on the tagName for easier registration
  static tagName = 'df-badge';

  // Defining element styles without a decorator
  // These are injected into the shadowRoot so they aren't applied globally
  static styles = [
    css`
      :host {
        width: 100%;
        height: 100%;
        display: block;
      }

      div {
        background: gray;
        margin: auto;
        border: 1px solid white;
        box-shadow: 3px 3px 5px black; 
        border-radius: 50%;
        padding: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      svg {
        width: 80%;
        height: 80%;
      }

      path {
        fill: white;
      }

      .small {
        height: 50px;
        width: 50px;
      }

      .medium {
        height: 80px;
        width: 80px;
      }
      .large {
        height: 120px;
        width: 120px;
      }
      
      .gold {
        background: ${unsafeCSS(color(dfstyles.colors.dfgold).darken(0.2).hex())}
      }
      .silver {
        background: ${unsafeCSS(dfstyles.colors.dfsilver)}
      }
     .bronze {
        background: ${unsafeCSS(dfstyles.colors.dfbronze)}
      }
     .red{
        background: ${unsafeCSS(dfstyles.colors.dfred)}
      }
     .orange{
        background: ${unsafeCSS(dfstyles.colors.dforange)}
      }
     .yellow{
        background: ${unsafeCSS(dfstyles.colors.dfyellow)}
      }
     .green{
        background: ${unsafeCSS(dfstyles.colors.dfgreen)}
      }
     .blue{
        background: ${unsafeCSS(dfstyles.colors.dfblue)}
      }
     .indigo {
        background: ${unsafeCSS('#4B0082')}
      }
     .violet {
        background: ${unsafeCSS(dfstyles.colors.dfpurple)}
      }

 
      .
    `,
  ];

  // Defining element properties without a decorator
  static properties = {
    type: {
      type: String,
    },
    size: {
      type: String,
    },
  };

  // Properties defined above will have a getter/setter created on the component,
  // but we want to define their type and/or defaults on the component
  type?: BadgeType;
  size: 'small' | 'medium' | 'large' = 'medium';

  render() {
    const badgeElement = getBadgeElement(this.type);
    if (!badgeElement) return nothing;
    const classes = {
      small: this.size === 'small',
      medium: this.size === 'medium',
      large: this.size === 'large',
      gold: badgeElement.color == 'gold',
      silver: badgeElement.color == 'silver',
      bronze: badgeElement.color == 'bronze',
      red: badgeElement.color == 'red',
      orange: badgeElement.color == 'orange',
      yellow: badgeElement.color == 'yellow',
      green: badgeElement.color == 'green',
      blue: badgeElement.color == 'blue',
      indigo: badgeElement.color == 'indigo',
      violet: badgeElement.color == 'violet',
    };

    return html`<div class=${classMap(classes)}>${badgeElement.badge}</div> `;
  }
}

export function getBadgeElement(badge: BadgeType | undefined): BadgeElement | undefined {
  switch (badge) {
    case BadgeType.StartYourEngine:
      return StartYourEngine;
    case BadgeType.Wallbreaker:
      return Wallbreaker;
    case BadgeType.Tree:
      return Tree;
    case BadgeType.Nice:
      return Nice;
    case BadgeType.Sleepy:
      return Sleepy;
    default:
      console.warn(`Invalid icon name: ${badge}`);
      return undefined;
  }
}
/*
  Each Icon SVG below contains the entire definition.
  This is so each can have custom x/y/viewBox properties

  For example: The WithdrawIcon needs `viewBox="0 0 16 16"`
*/

const StartYourEngine: BadgeElement = {
  name: 'Start Your Engine!',
  description: 'You finished a Grand Prix',
  color: 'red',
  badge: svg` <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
	 viewBox="-223 25 512 512" style="enable-background:new -223 25 512 512;" xml:space="preserve">
<g transform="translate(0,0)">
	<path d="M148.3,41l-4.3,3.4c56.9,23,97.6,63.7,120.6,120.6l8.2-10.2c-20.1-54-55.3-91.5-106.9-113.9H148.3L148.3,41z M204.2,41
		c28.7,18.2,51.7,42.3,68.8,72V88.6c-11.6-18.4-26.4-34.3-44.5-47.6H204.2L204.2,41z M256.4,41c5.9,5.5,11.5,11.3,16.6,17.4V41
		H256.4z M118.3,55.3c-6.1-0.1-16,1.4-27.2,5.1c-15,4.9-32.7,12.8-50.1,21.9c-6.4,3.3-12.7,6.8-18.9,10.4
		c27.1,5.2,54.4,17.9,79.7,35.3l69.5-49.7c-14.4-9-30.5-16.4-48.2-22.3C122,55.5,120.4,55.3,118.3,55.3L118.3,55.3z M187,89.1
		l-70,50c9.8,7.7,19.2,16.1,28,24.9c8.8,8.8,17.2,18.2,24.9,28l50-70C210.4,109.7,199.4,98.6,187,89.1L187,89.1z M-6.4,107.2
		c-19.4,0.2-36.7,5.9-49.4,18.6c-2.6,2.6-4.9,5.4-7,8.4c7.7,0.3,15.3,0.9,22.7,1.6c8.6-7,20.3-10.7,34.3-10.7c9.4,0,19.8,1.6,31,4.8
		c29.9,8.7,64.1,29.2,94.4,59.5c30.3,30.3,50.8,64.5,59.5,94.4c8.7,29.9,5.5,54.2-8.6,68.3c-2.1,2.1-4.5,4-7.1,5.6
		c-1.4,7.5-3.2,15.8-5.3,22.1c9.5-3,18-7.9,25.1-15c20.3-20.3,22.9-52.4,13.1-86c-9.4-32.2-30.1-67-60.2-98.2l-0.1-0.1l-0.1-0.1
		c-1.2-1.3-2.4-2.5-3.7-3.7h0c-1.2-1.2-2.5-2.5-3.7-3.7l-0.1-0.1c0,0-0.1-0.1-0.1-0.1c-31.2-30.1-65.9-50.9-98.2-60.2
		C17.6,109,5.3,107.1-6.4,107.2L-6.4,107.2z M230.8,137.8l-49.7,69.5c17.4,25.4,30.1,52.7,35.3,79.8c3.6-6.3,7.1-12.6,10.4-19
		c9-17.4,17-35.1,21.9-50.1c4.9-15,5.9-27.5,4.4-32C247.3,168.2,239.8,152.2,230.8,137.8L230.8,137.8z M-84.9,152.1
		c-24.4-0.1-49.5,1.1-72.8,2.2c7.7,2.1,14.9,4,22.9,6.3c29.7,8.9,59.7,19.5,74.8,40.2l12.5,17.1l-21-2.9
		c-45.9-6.4-74.4,5-101.5,15.7c33.8,7.3,58.5,15.2,87.7,36.7l14,10.3l-16.4,5.5c-59.6,19.9-90.7,56.8-122.1,99.6V405
		c9.6-5.1,19.3-10.6,29.1-15.9c27.2-14.8,55.8-28.3,85.5-19.6l7.8,2.3l-1.6,8c-6.8,35-28,91.4-48,141.2h15.5
		c44.1-50.8,93.3-113.8,109-146.6l7.2-15l8.6,14.2c5.8,9.5,3.7,19.8,1,31.7S-0.2,431-5.1,445.2c-6.2,18-12.5,36.7-16.6,52.2
		c12.5-12.5,25.6-29.2,36.8-46.9C32,424.1,45.1,395.4,48.8,378l5-23.6l11.7,21.1c12.4,22.4,11,52.3,7,84.7
		c-2.4,19.3-5.8,39.6-8.9,59.7c14.9-22.3,32.6-48.4,48.3-77.4c24.8-45.8,43.3-96.9,34.8-143.9c-2.7-15.1-13.1-34-28.3-52.8
		c-21.2,6.5-42.4,20.9-62.9,40.8c17.1-29.9,23.5-50,37.2-65.2C68.1,232.1,35.5,251,1.4,284.2c19.7-25.3,40.1-64.1,53.9-91.5
		C35,208.3,3.9,224.1-15.9,236.5c9.8-15.1,30.5-43.7,36.5-67c-11-5-21.8-8.7-31.9-10.8l-0.1,0l-0.1,0
		C-31.8,153.8-57.7,152.2-84.9,152.1L-84.9,152.1z"/>
</g>
</svg>
 `,
};

const Wallbreaker: BadgeElement = {
  name: 'Wallbreaker',
  description: 'First Place in a Grand Prix',
  color: 'gold',
  badge: svg`<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
	 viewBox="0 0 560 560" style="enable-background:new 0 0 560 560;" xml:space="preserve">
<style type="text/css">
	.st0{fill:none;stroke:#ffffff;stroke-width:2;stroke-miterlimit:10;}
	.st1{fill:none;stroke:#ffffff;stroke-width:4;stroke-linecap:round;stroke-miterlimit:10;}
	.st2{fill:#FFFFFF;}
</style>
<g id="Layer_1">
	<g id="XMLID_13_">
		<g id="XMLID_4_">
			<path id="XMLID_64_" d="M335.3,203.6c-8-1.3-15.8-3.3-23.4-5.9c-3.8-1.3-7.6-2.8-11.2-4.6c-0.9-0.4-1.8-0.9-2.7-1.4l-1.3-0.7
				l-1.3-0.8c-0.8-0.6-1.7-1.3-2.5-1.9c-0.8-0.7-1.6-1.3-2.3-2c-5.9-5.5-11.1-11.6-16.3-17.6c-5.2-6.1-10.2-12.2-15.2-18.3
				c-5.1-6.1-10.1-12.2-15.3-18.2c-5.2-6-10.4-11.9-16.1-17.2c-1.4-1.3-2.9-2.6-4.4-3.7l-1.1-0.8l-1.2-0.7l-0.3-0.2
				c0,0-0.2-0.1-0.3-0.2l-0.7-0.3c-0.4-0.2-0.9-0.5-1.3-0.7c-3.5-1.7-7.1-3.3-10.7-4.8c-7.3-3.1-14.7-5.9-22.1-8.6
				c-7.4-2.7-14.9-5.3-22.5-7.8c-7.5-2.4-15.1-4.8-22.7-6.8c-3.8-1-7.6-1.9-11.5-2.6c-1.9-0.3-3.8-0.7-5.7-0.7c-0.5,0-0.9,0-1.3,0
				l-0.3,0l-0.1,0c0,0,0,0-0.1,0l-0.2,0l-0.7,0.1c-0.9,0.2-1.8,0.3-2.7,0.6c-3.7,1-7.3,2.4-10.7,4.2c-3.4,1.8-6.7,3.9-9.5,6.4
				c-1.4,1.3-2.7,2.6-3.5,4.1c-0.4,0.7-0.7,1.5-0.7,2.1c0,0.2,0,0.8,0,1.3l0,1.5l0.4,11.9l0.8,23.8l1.6,47.6l-0.1-0.5l0,0
				c0-0.1,0,0,0,0.1c0,0.1,0.1,0.1,0.1,0.2c0.1,0.1,0.2,0.3,0.3,0.4c0.2,0.3,0.5,0.6,0.8,0.8c0.6,0.5,1.4,1,2.2,1.4
				c0.8,0.4,1.7,0.7,2.6,0.9c0.9,0.2,1.9,0.4,2.9,0.5l0.7,0.1l0.4,0l0.2,0l0-0.2c0,0-0.2-0.2-0.1-0.2h0h0h0.1l1.6,0.2
				c1,0,1.9,0,2.9-0.1c1-0.1,2-0.2,3-0.4c1-0.2,2-0.3,3-0.5c2-0.4,4-0.9,6.1-1.4c2-0.5,4-1.1,6-1.6c2-0.6,4-1.2,6-1.8
				c2-0.6,4-1.3,5.9-2l0,0c1-0.4,2.2,0.2,2.5,1.2c0.4,1-0.2,2.2-1.2,2.5c-2,0.7-4,1.4-6.1,2c-2,0.6-4.1,1.3-6.1,1.8
				c-2,0.6-4.1,1.2-6.2,1.7c-2.1,0.5-4.1,1-6.3,1.4c-1,0.2-2.1,0.4-3.2,0.6c-1.1,0.2-2.2,0.3-3.2,0.5c-1.1,0.1-2.3,0-3.4,0l-1.6-0.2
				h-0.1l-0.1,0.2l-0.1,0.1l-0.2,0l-0.4,0l-0.8-0.1c-1.1-0.1-2.2-0.3-3.4-0.6c-1.1-0.3-2.2-0.6-3.3-1.1c-1.1-0.5-2.2-1.1-3.1-2
				c-0.5-0.4-1-0.9-1.4-1.4c-0.2-0.3-0.4-0.6-0.6-0.9c-0.1-0.2-0.2-0.3-0.3-0.5c-0.1-0.2-0.2-0.3-0.3-0.7l0,0l-0.1-0.2l0-0.2
				L90.9,133l-0.7-23.8l-0.4-11.9l0-1.5c0-0.5,0-0.9,0-1.7c0.1-1.4,0.6-2.5,1.2-3.5c1.2-2,2.6-3.5,4.2-4.9c3.1-2.8,6.6-5,10.2-6.9
				c3.6-1.9,7.4-3.5,11.4-4.5c1-0.3,2-0.5,3.1-0.7c0.9-0.2,2.3-0.3,3.3-0.2c2.2,0.1,4.2,0.4,6.1,0.8c4,0.7,7.9,1.7,11.7,2.7
				c7.7,2.1,15.3,4.4,22.9,6.9c7.6,2.5,15.1,5.1,22.5,7.8c7.5,2.7,14.9,5.6,22.2,8.7c3.7,1.6,7.3,3.1,11,5c0.5,0.2,0.9,0.5,1.3,0.7
				l0.7,0.3c0.1,0.1,0.2,0.1,0.4,0.2l0.3,0.2l1.4,0.8l1.3,0.9c1.7,1.2,3.2,2.6,4.7,3.9c3,2.7,5.7,5.6,8.4,8.6c2.7,2.9,5.3,5.9,7.9,9
				c5.2,6,10.2,12.2,15.3,18.3c5,6.1,10.1,12.2,15.2,18.3c5.2,6,10.4,12,16.1,17.2c0.7,0.7,1.5,1.3,2.2,1.9c0.8,0.6,1.4,1.1,2.2,1.7
				l1.2,0.8l1.3,0.7c0.9,0.4,1.7,0.9,2.6,1.3c3.5,1.7,7.1,3.1,10.9,4.3c7.4,2.4,15.1,4.2,22.8,5.3l0,0c1.1,0.2,1.9,1.2,1.7,2.3
				C337.4,203,336.4,203.8,335.3,203.6C335.3,203.6,335.3,203.6,335.3,203.6z"/>
		</g>
		<g id="XMLID_10_">
			<path id="XMLID_14_" d="M480,417l-22.9-26.1l-12.6-52.1l44-21.5l45.7,4.5l-46.2-13l-33.2,6.7l12.1-45.7l48.4-30.5l1.8-25.6
				l13.9-15.2l-24.2,13.5l-3.6,24.7l-35.9-10.8l-17.5-68.2l15.7-26l47.5,2.2l-32.7-20.2l-25.6,11.2l-35.4-37.7l20.6-42.2L427.5,20
				l4,22.9l-31.8,67.3l-24.2-50.2l-22.9-29.2l5.8-19.7l-18.8,23.8l22,35.9l-11.2,55.2l-14.3-35.4l-40.7-19.7l32.5,30.5v37.2l-11-8.1
				l-21.8,5.8h16.6l-15.5,30.6l20,27.9l2,5.8c15.3,4.5,36.2,10.1,50,11.2c24.2,2,81.6-4.5,81.6-4.5l9.7,27.5l32.9,11.1l-34.2,19.1
				l-22.6,76.6l10.9,47.4c0,0-22.7-8.1-69.4-9.9c-23.8-0.9-41.6,0.9-53.3,2.8l0,0l7.6,21.4L297,414.7l-29.6-1.8l26,15.2l35-5.4
				l-16.6,21.1l18.4,24.2l-15.2,33.2l-47.5,5.8l56.5,3.6l14.8-34.1l33.2-16.6l19.3,46.6l-13,44.4l22-42.2l-9-26l29.6-8.1l27.4,28.3
				l-6.7-24.2l38.1-50.2l30-3.6l24.7,23.8L515.9,417H480z"/>
			<path id="XMLID_16_" d="M321.6,209.8l0.1,0.2C321.7,209.8,321.7,209.7,321.6,209.8L321.6,209.8z"/>
		</g>
		<g id="XMLID_1_">
			<path id="XMLID_62_" d="M327,314.2c0,0-0.1,2.4-0.3,6.9c-0.2,4.5-0.7,11.1-2,19.4c-0.7,4.2-1.6,8.7-2.9,13.7
				c-1.3,4.9-3.1,10.2-5.6,15.5c-1.2,2.7-2.8,5.3-4.5,8c-1.8,2.6-3.7,5.2-6.2,7.6l-0.9,0.9l-1,0.8c-0.7,0.5-1.3,1.1-2,1.7
				c-1.4,1-2.8,2.1-4.4,2.9c-1.5,0.9-3.2,1.6-4.8,2.3c-0.9,0.3-1.7,0.6-2.6,0.8c-0.4,0.1-0.8,0.3-1.3,0.4l-1.3,0.3
				c-1.8,0.4-3.5,0.8-5.3,1.2c-1.8,0.4-3.6,0.7-5.5,1c-1.9,0.3-3.7,0.7-5.6,1c-1.9,0.3-3.8,0.6-5.7,0.8c-7.7,1.1-15.7,2-23.9,2.9
				c-16.4,1.6-33.8,2.9-51.8,3.7c-9,0.4-18.1,0.8-27.4,0.9c-9.3,0.2-18.6,0.3-28.2-0.1c-4.9-0.3-9.5-1-14.3-1.7
				c-4.7-0.7-9.5-1.6-14.2-3.2c-4.7-1.6-9.2-3.8-13.2-7c-4-3.1-7.2-7.1-9.8-11.3l-1-1.6c-0.3-0.5-0.6-1.1-0.9-1.6l-1.7-3.2
				c-1.1-2.1-2.3-4.2-3.5-6.2c-2.4-4.1-4.8-8.1-7.5-12c-2.7-3.8-5.6-7.6-8.6-11.3c-1.5-1.8-3-3.7-4.4-5.5l-0.3-0.3l-0.3-0.4
				l-0.5-0.7l-1.1-1.4l-0.5-0.7l-0.5-0.8l-1-1.6c-1.3-2.2-2.3-4.4-3.2-6.8c-0.4-1.2-0.8-2.4-1-3.6c-0.2-1.2-0.5-2.4-0.6-3.7
				c-0.7-4.9-0.1-9.8,0.2-14.3c0.2-2.3,0.3-4.5,0-6.5l-0.1-0.8c0-0.1,0,0.1,0-0.1l0-0.2l-0.1-0.4c-0.1-0.5-0.2-1.2-0.4-1.5
				c-0.2-0.4-0.2-0.8-0.5-1.2c-0.2-0.4-0.4-0.8-0.7-1.2c-1.1-1.5-2.8-2.9-4.5-4.4c-1.7-1.5-3.5-3.2-5-5.1c-3.1-3.7-5.2-7.9-7-12.1
				c-0.4-1.1-0.8-2.1-1.2-3.2l-0.3-0.9l-0.2-0.8c-0.2-0.5-0.3-1-0.5-1.6c-0.6-2.3-1-4.6-1.1-7c-0.1-2.3,0.1-4.7,0.6-7
				c0.5-2.3,1.3-4.5,2.2-6.5c1.9-4.1,4.4-7.5,6.1-11c1.8-3.4,2.5-6.9,1.8-10.2c-0.2-0.8-0.4-1.9-0.7-2.8c-0.3-0.9-0.5-1.8-0.8-2.6
				c-0.6-1.8-1.3-3.5-2.1-5.2c-0.8-1.8-1.6-3.5-2.5-5.2c-0.8-1.8-1.6-3.6-2.2-5.5c-1.3-3.8-1.9-7.6-2.3-11.3
				c-0.1-0.9-0.1-1.8-0.2-2.8l0-0.7l0-0.3l0-0.2c0,0.1,0-0.3,0-0.3l0-1.3c0-0.4,0.1-1,0.1-1.5c0.1-0.5,0.1-1.1,0.2-1.5
				c0.2-0.9,0.4-1.9,0.7-2.8c1.1-3.6,2.7-6.8,4.6-9.7c1.8-2.9,3.8-5.5,5.9-7.9c2.1-2.4,4.2-4.6,6.4-6.6c2.2-2.1,4.4-3.9,6.6-5.6
				c2.2-1.7,4.3-3.3,6.4-4.8c4.2-2.9,8.2-5.4,12-7.4c1.9-1,3.7-1.9,5.5-2.6c0.5-0.2,0.9-0.3,1.4-0.5c0.4-0.1,0.9-0.3,1.3-0.4
				c0.8-0.3,1.7-0.5,2.5-0.7c3.2-0.9,6-1.5,8.2-1.9c4.6-0.8,7.3-0.7,7.5-0.6c1.1,0.3,1.7,1.4,1.3,2.5s-1.4,1.7-2.5,1.3
				c0.1,0-0.3-0.2-1.3-0.5c-1-0.3-2.7-0.5-4.8-0.5c-2.2,0.1-4.9,0.4-8,1.4c-0.8,0.2-1.5,0.5-2.4,0.8c-0.4,0.1-0.8,0.3-1.3,0.4
				c-0.4,0.1-0.8,0.3-1.2,0.5c-1.7,0.7-3.4,1.6-5.2,2.7c-7.1,4.3-15,10.7-23.1,18.8c-4,4.1-8.1,8.6-11.5,13.9
				c-1.7,2.6-3.1,5.5-4,8.5c-0.3,0.7-0.4,1.5-0.6,2.3c-0.1,0.4-0.1,0.7-0.1,1.1c0,0.4-0.1,0.7-0.1,1.2l0,1.3l0.1,1.2
				c0.1,0.9,0.1,1.7,0.2,2.6c0.3,3.5,0.9,6.9,2.1,10.3c0.5,1.7,1.2,3.3,2,5c0.8,1.7,1.6,3.5,2.5,5.2c0.8,1.8,1.6,3.7,2.3,5.7
				c0.4,1,0.6,2,0.9,3c0.3,1,0.5,1.9,0.7,3c0.5,2.2,0.5,4.6,0.1,6.9c-0.4,2.3-1.3,4.4-2.3,6.4c-2,3.9-4.4,7.3-6.1,10.9
				c-1.7,3.6-2.6,7.4-2.4,11.3c0.1,1.9,0.4,4,0.9,5.9c0.1,0.5,0.3,1,0.5,1.5l0.2,0.8l0.3,0.7c0.4,1,0.7,2,1.1,3
				c1.6,3.9,3.6,7.7,6.2,11c1.3,1.7,2.8,3.1,4.5,4.6c1.6,1.5,3.5,3,5.1,5.2c0.4,0.5,0.8,1.1,1.1,1.7c0.4,0.6,0.6,1.3,0.8,2
				c0.3,0.8,0.3,1.2,0.5,1.8l0.1,0.4l0,0.2c0-0.1,0.1,0.3,0.1,0.4l0.1,0.9c0.3,2.5,0.1,5-0.1,7.3c-0.4,4.7-0.9,9.1-0.3,13.5
				c0.1,1.1,0.3,2.2,0.5,3.2c0.2,1.1,0.6,2.1,0.9,3.2c0.8,2.1,1.7,4.1,2.8,6c2.2,3.8,5.4,7.4,8.3,11.2c2.9,3.7,5.9,7.6,8.6,11.5
				c2.8,4,5.3,8.1,7.7,12.3c1.2,2.1,2.3,4.2,3.5,6.3l1.7,3.2c0.3,0.5,0.6,1.1,0.9,1.6l0.9,1.5c2.3,4,5.2,7.5,8.8,10.4
				c3.5,2.9,7.7,4.9,12,6.4c4.4,1.5,9,2.4,13.6,3.1c4.6,0.7,9.4,1.4,13.9,1.7c9.3,0.5,18.7,0.5,27.9,0.4c9.2-0.1,18.3-0.4,27.3-0.7
				c17.9-0.7,35.3-1.8,51.6-3.3c8.2-0.8,16.1-1.6,23.8-2.7c1.9-0.3,3.8-0.5,5.7-0.8c1.9-0.3,3.7-0.6,5.5-0.9c1.8-0.3,3.6-0.6,5.4-1
				c1.8-0.4,3.6-0.8,5.3-1.2l1.3-0.3c0.4-0.1,0.8-0.2,1.2-0.4c0.8-0.3,1.6-0.5,2.5-0.8c1.6-0.7,3.2-1.3,4.6-2.1
				c1.5-0.7,2.8-1.8,4.2-2.7c0.6-0.5,1.3-1,1.9-1.6l0.9-0.8l0.8-0.9c2.3-2.2,4.2-4.7,6-7.2c1.7-2.6,3.2-5.1,4.4-7.7
				c2.5-5.2,4.3-10.3,5.7-15.2c1.4-4.8,2.3-9.4,3-13.5c1.4-8.2,1.9-14.8,2.2-19.3c0.2-4.5,0.2-6.9,0.2-6.9c0-0.3,0.4-0.5,0.7-0.6
				C326.6,313.6,327,313.9,327,314.2L327,314.2z"/>
		</g>
		<path id="XMLID_6_" d="M116.7,182.8c0,0,36.1-15,36.3-32.3c0.2-17.3,1.8-22.2,0-33.6s16.8,31.6,21.5,45.3
			c4.7,13.7,18.4-7.8,21.1-2.2s-4.3,58.1-2.2,71.3c2,13.2,19.1,44.9,45.3,42.6c26.2-2.2,41.3-17.5,50.7-21.5c9.4-4-19.5,9.4-17,21.6
			s18.8,22.8,18.9,25.5c0.1,2.7-41.1-19.1-54.8-14.4c-13.7,4.7-17,25-11.8,45.1c1.9,7.4-28.6,30.9-24.5,40.1
			c7.1,16,48.9,1.3,48.9,1.3s49.1-5.6,56.5-19.7s10.8-29.4,10.3-35c-0.4-5.6,5.6,26.2-11.7,57.4s-184.8,18.6-187.5,17
			c-2.7-1.6-18.4-3.6-9-19.3s84.8-32.7,93.3-47.5c8.5-14.8-12.6-43.5-12.6-43.5s7.6-12.3,0-29.6S176.4,237,176.4,237
			s6.1-13.9,0-30.9C170.3,189.1,162.7,183.2,116.7,182.8z"/>
		<path id="XMLID_7_" class="st0" d="M94.8,176.2c3.9-5.4,9.3-9.6,15.5-11.9c3.8-1.5,8.2-2.5,10.5-5.8c2.8-4,1.4-9.3,0.5-14
			c-1.3-6.7-1.7-13.6-1-20.3l-0.4-1.3c-8.4-2-17,1.7-24.8,5.4"/>
		<path id="XMLID_8_" class="st1" d="M61.2,203.9c0,0,20-16.2,36.1-19.7"/>
		<path id="XMLID_5_" class="st1" d="M73.3,267.8c0,0,21.2-25.3,38.3-28.7s65.8-4,65.8-4"/>
		<path id="XMLID_9_" class="st1" d="M94.3,323.2c0,0,17.3-24.7,34.5-33.6s59.7-8.5,59.7-8.5"/>
		<path id="XMLID_12_" class="st0" d="M332.3,383.1c-9.5-0.8-19.1,0-28.3,2.4"/>
		<path id="XMLID_15_" class="st0" d="M297.4,190.6c1.7,4.4,3.3,8.8,5,13.3c0.6,1.6,1.2,3.2,1.7,4.9c1.6,5.4,2,11.2,1.1,16.8"/>
		<g id="XMLID_28_">
			<g id="XMLID_82_">
				<path class="st2" d="M137.3,315.2c1.9,0,1.9-3,0-3C135.4,312.2,135.4,315.2,137.3,315.2L137.3,315.2z"/>
			</g>
		</g>
	</g>
</g>
<g id="Layer_2">
</g>
</svg>

`,
};

const Nice: BadgeElement = {
  name: 'Nice',
  description: 'You finished a Grand Prix in exactly 69 moves',
  color: 'violet',
  badge: svg`<svg
   xmlns:dc="http://purl.org/dc/elements/1.1/"
   xmlns:cc="http://creativecommons.org/ns#"
   xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#"
   xmlns:svg="http://www.w3.org/2000/svg"
   xmlns="http://www.w3.org/2000/svg"
   version="1.1"
   width="600"
   height="600"
   viewBox="0 0 600 600"
   id="Layer_1"
   xml:space="preserve"
   style="overflow:visible"><metadata
     id="metadata20"><rdf:RDF><cc:Work
         rdf:about=""><dc:format>image/svg+xml</dc:format><dc:type
           rdf:resource="http://purl.org/dc/dcmitype/StillImage" /><dc:title></dc:title></cc:Work></rdf:RDF></metadata><defs
     id="defs18" /><path
     d="M 300,300"
     id="path7" /><path
     d="m 300,10.00293 c 23.56445,20.075195 54.11719,32.191406 87.5,32.191406 33.38281,0 63.93555,-12.119141 87.5,-32.194336 l 115,115 c -21.16211,30.08105 -33.58789,66.75391 -33.58789,106.32617 0,28.46387 6.42676,55.42481 17.91016,79.5127 C 584.24414,331.66406 590,355.39453 590,380 590,468.36719 518.36719,540 430,540 H 404.88672 C 362.54199,540 324.75098,559.49512 300,590 275.24854,559.49512 237.45801,540 195.11328,540 H 169.99951 C 81.632813,540 9.9995117,468.36719 9.9995117,380 c 0,-24.60547 5.7563473,-48.33594 15.6787113,-69.16113 11.482422,-24.08789 17.910156,-51.04883 17.910156,-79.5127 C 43.588379,191.75391 31.161621,155.08105 10,125 L 125,10 c 23.56445,20.075195 54.1167,32.194336 87.5,32.194336 33.38281,0 63.93555,-12.116211 87.5,-32.191406 z"
     id="path11"
     style="fill:#ffffff;fill-opacity:0;fill-rule:evenodd" /><g
     id="g3886"><path
       d="m 300,10.00293 c 23.56445,20.075195 54.11719,32.191406 87.5,32.191406 33.38281,0 63.93555,-12.119141 87.5,-32.194336 l 115,115 c -21.16211,30.08105 -33.58789,66.75391 -33.58789,106.32617 0,28.46387 6.42676,55.42481 17.91016,79.5127 C 584.24414,331.66406 590,355.39453 590,380 590,468.36719 518.36719,540 430,540 H 404.88672 C 362.54199,540 324.75098,559.49512 300,590 275.24854,559.49512 237.45801,540 195.11328,540 H 169.99951 C 81.632813,540 9.9995117,468.36719 9.9995117,380 c 0,-24.60547 5.7563473,-48.33594 15.6787113,-69.16113 11.482422,-24.08789 17.910156,-51.04883 17.910156,-79.5127 C 43.588379,191.75391 31.161621,155.08105 10,125 L 125,10 c 23.56445,20.075195 54.1167,32.194336 87.5,32.194336 33.38281,0 63.93555,-12.116211 87.5,-32.191406 z"
       id="path13"
       style="fill:#ffffff;fill-opacity:1;fill-rule:evenodd;stroke:#000000;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:3.863693" /></g><g
     id="text1343"
     style="font-size:447.76119995px;font-style:normal;font-variant:normal;font-weight:normal;font-stretch:normal;text-align:center;line-height:125%;writing-mode:lr-tb;text-anchor:middle;font-family:Roadgeek 2005 Series D"><path
       d="m 277.30436,351.08209 c -2.3e-4,12.83589 -2.68679,24.92543 -8.0597,36.26866 -5.07485,11.04481 -12.23902,20.74629 -21.49254,29.10447 -8.95541,8.05972 -19.70167,14.47762 -32.2388,19.25374 -12.53747,4.77611 -25.9703,7.16417 -40.29851,7.16417 -14.32848,0 -27.7613,-2.38806 -40.29851,-7.16417 -12.53739,-5.07462 -23.4329,-11.94029 -32.68657,-20.59702 -8.955269,-8.65669 -16.119441,-18.80593 -21.492533,-30.44776 -5.074659,-11.64173 -7.61197,-24.17903 -7.61194,-37.61194 -3e-5,-20.89541 2.686535,-41.64166 8.059701,-62.23881 5.671599,-20.59684 13.582039,-40.29831 23.731342,-59.10448 10.14918,-18.80574 22.23872,-36.11915 36.26866,-51.9403 14.32825,-16.11912 30.14913,-29.9997 47.46269,-41.64179 l 24.1791,40.29851 c -9.25389,7.16444 -17.46284,14.17936 -24.62686,21.04478 -6.86581,6.5674 -13.28372,13.43306 -19.25374,20.59701 -5.67176,7.1644 -10.89563,14.77633 -15.67164,22.83582 -4.77622,7.76139 -9.40308,16.4181 -13.8806,25.97015 8.65662,-1.49236 15.22377,-2.38788 19.7015,-2.68657 4.776,-0.29833 10.14913,-0.44758 16.1194,-0.44776 13.7312,1.8e-4 26.71626,2.38824 38.95522,7.16418 12.53714,4.77629 23.43265,11.34345 32.68657,19.7015 9.55203,8.05984 17.01471,17.76132 22.38806,29.10447 5.37291,11.04489 8.05947,22.83592 8.0597,35.37314 m -47.46268,-0.89552 c -1.9e-4,-5.67155 -1.49273,-11.19394 -4.47762,-16.56717 -2.68674,-5.37302 -6.56733,-9.99988 -11.64179,-13.8806 -4.77628,-3.88047 -10.59717,-7.0148 -17.46268,-9.40298 -6.56731,-2.38793 -13.58223,-3.58196 -21.04478,-3.58209 -7.46281,1.3e-4 -14.47773,1.19416 -21.04478,3.58209 -6.26876,2.38818 -11.94039,5.52251 -17.01492,9.40298 -4.77621,3.88072 -8.6568,8.50758 -11.64179,13.8806 -2.68665,5.37323 -4.02993,10.89562 -4.02985,16.56717 -8e-5,5.97023 1.3432,11.64186 4.02985,17.01492 2.98499,5.3732 6.86558,10.14932 11.64179,14.32836 5.07453,3.88065 10.74616,7.01497 17.01492,9.40299 6.56705,2.3881 13.58197,3.58213 21.04478,3.58208 7.46255,5e-5 14.47747,-1.19398 21.04478,-3.58208 6.567,-2.38802 12.23864,-5.52234 17.01492,-9.40299 5.07445,-4.17904 9.1043,-8.95516 12.08955,-14.32836 2.98489,-5.37306 4.47743,-11.04469 4.47762,-17.01492"
       id="path4684"
       style="letter-spacing:-13.84615421;fill:black" /><path
       d="m 526.69282,227.94776 c -2.3e-4,20.5972 -2.83605,41.1942 -8.50746,61.79104 -5.37336,20.29864 -13.13454,39.85086 -23.28358,58.65672 -10.14945,18.80604 -22.38824,36.26871 -36.71642,52.38806 -14.32851,16.11943 -30.14939,30.14926 -47.46269,42.08955 l -22.83582,-40.2985 c 8.95512,-7.16414 16.86556,-14.02981 23.73134,-20.59702 7.16406,-6.86561 13.58196,-13.88053 19.25374,-21.04478 5.67149,-7.16409 10.89537,-14.77602 15.67164,-22.83582 4.77596,-8.05959 9.40282,-17.01481 13.88059,-26.86567 -8.65687,1.49266 -15.37328,2.53744 -20.14925,3.13433 -4.47775,0.59714 -9.70163,0.89564 -15.67164,0.89552 -14.02997,1.2e-4 -27.31354,-2.38793 -39.85075,-7.16418 -12.23888,-4.77598 -22.98514,-11.19389 -32.2388,-19.25373 -9.25379,-8.35805 -16.56721,-18.05953 -21.9403,-29.10448 -5.37317,-11.34309 -8.05973,-23.28338 -8.05971,-35.82089 -2e-5,-12.8356 2.68654,-24.77588 8.05971,-35.8209 5.37309,-11.34302 12.53726,-21.0445 21.49253,-29.10448 9.25367,-8.35792 19.99993,-14.92508 32.23881,-19.70149 12.53721,-4.77582 25.97003,-7.16387 40.29851,-7.16418 14.32821,3.1e-4 27.76103,2.53762 40.29851,7.61194 12.53713,4.77641 23.28338,11.49283 32.2388,20.14926 9.25352,8.35847 16.41769,18.35846 21.49254,30 5.3729,11.64202 8.05947,24.32858 8.0597,38.0597 m -47.46269,-4.02985 c -1.8e-4,-5.67142 -1.49272,-11.19381 -4.47761,-16.56717 -2.98525,-5.3729 -7.0151,-9.99976 -12.08955,-13.88059 -4.77628,-3.88035 -10.44792,-7.01468 -17.01493,-9.40299 -6.56731,-2.3878 -13.58222,-3.58183 -21.04477,-3.58209 -7.46281,2.6e-4 -14.47773,1.19429 -21.04478,3.58209 -6.26876,2.0898 -11.9404,5.07488 -17.01492,8.95522 -4.77621,3.88084 -8.6568,8.5077 -11.64179,13.8806 -2.68665,5.37336 -4.02993,11.045 -4.02986,17.01493 -7e-5,5.97035 1.34321,11.64199 4.02986,17.01492 2.98499,5.07482 6.86558,9.55243 11.64179,13.43284 5.07452,3.88078 10.74616,7.0151 17.01492,9.40298 6.56705,2.08973 13.58197,3.1345 21.04478,3.13433 7.46255,1.7e-4 14.47746,-1.0446 21.04477,-3.13433 6.86552,-2.38788 12.68641,-5.5222 17.46269,-9.40298 5.07445,-3.88041 8.95505,-8.35802 11.64179,-13.43284 2.98489,-5.37293 4.47743,-11.04457 4.47761,-17.01492"
       id="path4686"
       style="letter-spacing:-13.84615421;fill:black" /></g></svg>`,
};

const Tree: BadgeElement = {
  name: 'Big Trees',
  description: 'You finished a Grand Prix in exactly 420 moves',
  color: 'green',
  badge: svg`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"></circle><g class="" transform="translate(0,0)" style=""><path d="M222.156 20.72c-1.423.01-2.843.065-4.25.155-20.084 1.29-37.527 10.342-46.375 27.03l17.626 12.532h-29.312c-10.333-5.256.412-.266-11.375-5.28-23.158-13.118-48.787-9.64-67.25 2.812-20.062 13.527-30.734 35.806-20.25 60.093l3.093 7.156-6.47 4.31c-21.435 14.36-32.997 30.393-37.124 48.126C16.34 195.39 19.65 215.64 31 238.5l2.47 5.03-3.25 4.564c-13.796 19.38-8.88 38.668 5.968 51.03 10.043 8.364 19.48 9.982 29.656 7.407 10.177-2.574 21.15-10.15 30.72-21.874l2.655-3.25 4.155-.187c27.728-1.2 49.69-11.635 63.97-27.72 14.28-16.085 21.22-37.98 17.718-63.906L184.03 182l7.25-2.5c8.066-2.768 14.73-8.15 18.814-15.53l5.28-9.564 8.626 6.72c6.39 4.976 22.988 8.397 31.375 5.968l10.28-2.97 1.564 10.595c4.3 29.362 24.393 52.534 50.06 60.155l8.25 2.438-1.75 8.437c-3.162 14.99 1.497 26.075 10.44 33.406 8.94 7.332 22.793 10.683 38.75 6.438l6.217-1.656 3.782 5.25c13.513 18.845 29.945 28.087 46.342 29.562 16.398 1.475 33.29-4.745 48.032-19.594 12.624-12.716 16.654-26.51 15.062-40.437-1.59-13.93-9.318-28.12-21.75-39.533l-8.5-7.812 9.406-6.688c7.878-5.6 10.74-15.92 8.907-25.5-1.833-9.58-7.527-16.677-16.345-17.78l-9.375-1.157 1.28-9.375c5.842-42.567-13.62-77.648-45.342-97.656-31.723-20.01-75.673-24.486-118.782-3.095l-6.72 3.344-4.717-5.876c-13.464-16.796-34.023-25.768-54.033-26.782-1.43-.072-2.857-.104-4.28-.093zm-1.22 160.31c-4.003 4.804-8.867 8.724-14.25 11.782 11.968 29.99 18.83 60.783 23.033 93.532-4.555 11.237-9.676 22.38-15.5 33.437l-.157.064c-8.54-19.537-18.073-38.957-31.188-55.75-.51.607-1.035 1.218-1.563 1.812-3.628 4.088-7.652 7.838-12 11.25 12.034 15.867 21.048 34.83 29.72 54.938 7.724 43.39 6.386 91.267-2.782 134.812-26.727 5.073-55.198 15.22-88.594 30.5h252.938c-32.428-15.282-60.268-25.46-86.688-30.53-13.95-24.116-21.036-50.13-23.25-76 9.692-45.174 28.504-71.692 57-99.282-19.276 7.68-37.422 17.31-52.687 31.937.702-3.41 1.488-6.766 2.31-10.092 9.476-24.123 23.87-45.82 42.94-65.407-5.784-2.705-11.25-5.994-16.283-9.874-12.71 13.552-23.555 28.128-32.312 43.78 7.156-19.187 12.88-38.422 18.063-57.592-5.443-6.556-10.03-13.933-13.563-21.938-4.167 16.03-8.512 31.946-13.5 47.72-4.637-23.334-11.12-46.08-20.375-68.47-.438-.204-.88-.41-1.313-.625z" fill="#000000" fill-opacity="1"></path></g></svg>`,
};

const Sleepy: BadgeElement = {
  name: 'Sleepy',
  description: 'You finished a Grand Prix in over 24 hours',
  color: 'blue',
  badge: svg`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" <g class="" transform="translate(0,0)" style=""><path d="M25 169v286h30V169H25zm94.1 52.2c-2.9 0-5.8.5-8.3 1.2-6.7 1.9-11.03 5.6-12.96 10.5-1.94 4.9-1.25 10.6 2.36 16.5 3.6 5.9 10 11.6 18.6 14.9 8.6 3.4 17.2 3.6 23.8 1.7 6.7-1.9 11-5.5 13-10.5 1.9-4.9 1.2-10.6-2.4-16.5-3.6-5.9-10.1-11.5-18.6-14.9-4.8-1.9-9.7-2.8-14.2-2.9h-1.3zM73 253.4V295h61.8c1.7-3.4 3.5-6.8 5.5-10.2-9 1.1-18.7-.1-28.1-3.7-12-4.7-21.58-12.8-27.39-22.4-.11-.2-.21-.4-.32-.6L73 253.4zm108.5 4.5c-17.6 19.3-32.4 46-40.5 67.9-4.2 11.5-6.5 21.9-6.7 28.2 0 2.5.2 4 .5 4.8 57.4 7.3 98.1.3 150.6-15.4l2-.6 2 .3c56.3 8.6 111.6 10 161.6 1.6v-35.6c-67.7-29-178.4-46.5-269.5-51.2zM469 281v174h18V281h-18zM73 313v30h44.5c1.3-7.2 3.6-15 6.6-23.4.8-2.2 1.7-4.4 2.6-6.6H73zm0 48v30h378v-27.9c-51 8.1-106.5 6.4-162.3-1.9-53.2 15.7-97.7 23.2-157.8 15.3-5.3-.7-10.2-4.9-12.3-9.3-.9-2-1.5-4.1-1.9-6.2H73z" fill="#000000" fill-opacity="1"></path></g></svg>
  `,
};
